"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { formatDate } from "@gigpro/utils/datetime";
import { endOfMonth, set, startOfMonth } from "date-fns";
import type React from "react";
import { Calendar as CalendarIcon } from "../../Icons";
import { Calendar } from "../Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";

export interface DatePickerProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value" | "children"> {
  ref?: React.Ref<HTMLButtonElement>;
  value?: Date;
  onValueChange: (value: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /**
   * Restrict selection to a single weekday (`Date#getDay()` index, 0 = Sunday);
   * every other weekday renders disabled. For grids that only have valid dates
   * on one weekday — e.g. a weekly billing period, which can only end the day
   * before the customer's work-week end.
   */
  onlyDayOfWeek?: number;
  compact?: boolean;
}

const ALL_DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

const formatValue = (value: Date, compact: boolean) => formatDate(value, compact ? "MM/dd/yyyy" : "MMM d, yyyy");

export const DatePicker = ({
  ref,
  value,
  onValueChange,
  compact = false,
  placeholder = "MM/DD/YYYY",
  disabled,
  invalid,
  minDate,
  maxDate,
  onlyDayOfWeek,
  className,
  ...props
}: DatePickerProps) => (
  <Popover modal>
    <PopoverTrigger
      ref={ref}
      className={cn(
        "inline-flex w-full items-center justify-start rounded-sm border-2 px-3 py-2 text-left text-large/5 font-regular outline-none focus:border-brand data-[state=open]:border-brand",
        disabled && "bg-background [&>svg]:text-gray",
        (!invalid || disabled) && "border-medium-gray",
        invalid && !disabled && "border-error",
        !value && "text-gray",
        className,
      )}
      disabled={disabled}
      {...props}
    >
      <span className="truncate">{value ? formatValue(value, compact) : placeholder}</span>
      <CalendarIcon className="ml-auto size-4 shrink-0 text-brand" />
    </PopoverTrigger>

    <PopoverContent className="w-auto p-0">
      <Calendar
        mode="single"
        selected={value ?? undefined}
        onSelect={(newValue) => {
          if (!value || !newValue) return onValueChange(newValue);
          onValueChange(
            set(newValue, {
              hours: value.getHours(),
              minutes: value.getMinutes(),
              seconds: value.getSeconds(),
              milliseconds: value.getMilliseconds(),
            }),
          );
        }}
        autoFocus
        defaultMonth={value}
        startMonth={minDate && startOfMonth(minDate)}
        endMonth={maxDate && endOfMonth(maxDate)}
        disabled={[
          ...(minDate ? [{ before: minDate }] : []),
          ...(maxDate ? [{ after: maxDate }] : []),
          ...(onlyDayOfWeek === undefined
            ? []
            : [{ dayOfWeek: ALL_DAYS_OF_WEEK.filter((day) => day !== onlyDayOfWeek) }]),
        ]}
      />
    </PopoverContent>
  </Popover>
);

DatePicker.displayName = "DatePicker";
